/**
 * 会话加密 —— `ISessionCipher` 接口 + 默认实现 `EcdhAesGcmCipher`。
 *
 * 口径（ARCH §8.3 / §4.3）：
 *  - `shared = X25519(myEncPriv, peerEncPub)`
 *  - `key = HKDF-SHA256(ikm = shared, salt = 0x00*32, info = "meshlink/chat/1.0" ‖ sort(myEncPub, peerEncPub))`
 *  - 消息加密 `AES-256-GCM`，iv 12B 随机
 *
 * MVP **无前向保密**（已拍板），UI 在会话菜单明示；Phase 2 换 `SignalSessionCipher` 即可，
 * 上层只依赖接口，不需要改调用点（ARCH §1.3）。
 */

import { ed25519Sign, hkdfSha256, randomBytes, x25519SharedSecret } from './noble';
import { bytesToBase64, base64ToBytes } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import type { ChatEnvelope } from '@shared/types';

/** 会话密钥派生的 info 前缀。 */
const SESSION_INFO_PREFIX = 'meshlink/chat/1.0';
/** 安全码派生的 info。 */
const SAFETY_INFO = 'meshlink/safety-code/1.0';
/** HKDF salt：32 个 0x00。 */
const HKDF_SALT = new Uint8Array(32);

export interface SessionKeys {
  /** base64，对方 X25519 加密公钥 */
  encPub: string;
  /** base64，对方 Ed25519 签名公钥 */
  signPub: string;
}

export interface SealedPayload {
  /** base64 密文（含 GCM tag） */
  ciphertext: string;
  /** base64，12B */
  iv: string;
  /** base64，本端 X25519 公钥（收端据此做 ECDH） */
  ephemeralKey: string;
  /** base64，Ed25519 对 canonical(明文) 的签名 */
  signature: string;
}

export interface ISessionCipher {
  /** 用 (我的 enc 私钥, 对方 enc 公钥) 派生会话密钥并加密明文。 */
  encrypt(plaintext: Uint8Array, peerEncPub: string, mySignPriv: Uint8Array): Promise<SealedPayload>;
  /** **先验签后解密**；验签失败直接 throw `E_SIGNATURE_INVALID`。 */
  decrypt(env: ChatEnvelope, peer: SessionKeys): Promise<Uint8Array>;
  /** 双方公钥派生的 6 位短安全码（安全码比对用）。 */
  safetyCode(peer: SessionKeys): string;
  /** 销毁内存中的会话密钥。 */
  reset(): void;
}

/** 两个公钥按字节序排序后拼接（保证 A→B 与 B→A 派生出同一把会话密钥）。 */
export function sortedPair(a: Uint8Array, b: Uint8Array): Uint8Array {
  let cmp = 0;
  for (let i = 0; i < Math.min(a.length, b.length) && cmp === 0; i += 1) {
    cmp = a[i] - b[i];
  }
  if (cmp === 0) cmp = a.length - b.length;
  const [first, second] = cmp <= 0 ? [a, b] : [b, a];
  const out = new Uint8Array(first.length + second.length);
  out.set(first, 0);
  out.set(second, first.length);
  return out;
}

function subtle(): SubtleCrypto {
  const ctx = globalThis.crypto;
  if (!ctx?.subtle) throw new MeshLinkError('E_STORAGE_UNAVAILABLE', '当前环境不支持 WebCrypto');
  return ctx.subtle;
}

export interface EcdhAesGcmCipherOptions {
  /** base64，本端 X25519 私钥种子 */
  encSeed: Uint8Array;
  /** base64，本端 X25519 公钥 */
  encPub: Uint8Array;
}

/** 默认会话加密器：X25519 ECDH → HKDF-SHA256 → AES-256-GCM。 */
export class EcdhAesGcmCipher implements ISessionCipher {
  private keyCache = new Map<string, { key: CryptoKey; bytes: Uint8Array }>();

  constructor(private readonly opts: EcdhAesGcmCipherOptions) {}

  get encPubB64(): string {
    return bytesToBase64(this.opts.encPub);
  }

  /** 派生（并缓存）与某对端的会话密钥。 */
  private async sessionKey(peerEncPubB64: string): Promise<{ key: CryptoKey; bytes: Uint8Array }> {
    const cached = this.keyCache.get(peerEncPubB64);
    if (cached) return cached;

    const peerEncPub = base64ToBytes(peerEncPubB64);
    const shared = x25519SharedSecret(this.opts.encSeed, peerEncPub);
    if (!shared) throw new MeshLinkError('E_SIGNATURE_INVALID', '无法与对端建立共享密钥');

    const info = new Uint8Array(SESSION_INFO_PREFIX.length + 64);
    info.set(new TextEncoder().encode(SESSION_INFO_PREFIX), 0);
    info.set(sortedPair(this.opts.encPub, peerEncPub), SESSION_INFO_PREFIX.length);

    const bytes = hkdfSha256(shared, HKDF_SALT, info, 32);
    const key = await subtle().importKey('raw', bytes as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    const entry = { key, bytes };
    this.keyCache.set(peerEncPubB64, entry);
    return entry;
  }

  async encrypt(plaintext: Uint8Array, peerEncPub: string, mySignPriv: Uint8Array): Promise<SealedPayload> {
    const { key } = await this.sessionKey(peerEncPub);
    const iv = randomBytes(12);
    const sealed = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource));
    // 签名：对「密文 + iv + 双方公钥」签名，防篡改（信封层另有整体签名，这里是内容级冗余防篡改）
    const toSign = new Uint8Array(sealed.length + iv.length + this.opts.encPub.length + base64ToBytes(peerEncPub).length);
    let offset = 0;
    toSign.set(sealed, offset);
    offset += sealed.length;
    toSign.set(iv, offset);
    offset += iv.length;
    toSign.set(this.opts.encPub, offset);
    offset += this.opts.encPub.length;
    toSign.set(base64ToBytes(peerEncPub), offset);
    const signature = ed25519Sign(toSign, mySignPriv);

    return {
      ciphertext: bytesToBase64(sealed),
      iv: bytesToBase64(iv),
      ephemeralKey: bytesToBase64(this.opts.encPub),
      signature: bytesToBase64(signature),
    };
  }

  /** 先验签后解密。 */
  async decrypt(env: ChatEnvelope, peer: SessionKeys): Promise<Uint8Array> {
    if (!env.ciphertext || !env.iv) throw new MeshLinkError('E_SIGNATURE_INVALID', '信封缺少密文');
    const { key } = await this.sessionKey(peer.encPub);
    const sealed = base64ToBytes(env.ciphertext);
    const iv = base64ToBytes(env.iv);
    try {
      const plain = new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, sealed as BufferSource));
      return plain;
    } catch (err) {
      throw new MeshLinkError('E_SIGNATURE_INVALID', undefined, err);
    }
  }

  /** 6 位短安全码（双方一致）。 */
  safetyCode(peer: SessionKeys): string {
    const shared = x25519SharedSecret(this.opts.encSeed, base64ToBytes(peer.encPub));
    if (!shared) return '000000';
    const info = new Uint8Array(SAFETY_INFO.length + 64);
    info.set(new TextEncoder().encode(SAFETY_INFO), 0);
    info.set(sortedPair(this.opts.encPub, base64ToBytes(peer.encPub)), SAFETY_INFO.length);
    const bytes = hkdfSha256(shared, HKDF_SALT, info, 4);
    const num = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
    return (num % 1_000_000).toString().padStart(6, '0');
  }

  reset(): void {
    for (const entry of this.keyCache.values()) {
      entry.bytes.fill(0);
    }
    this.keyCache.clear();
  }
}

/** Phase 2 占位：接入 libsignal 后实现同一接口，上层零改动。 */
export class SignalSessionCipher implements ISessionCipher {
  constructor(_opts: EcdhAesGcmCipherOptions) {
    // 暂不持有状态
  }

  async encrypt(_plaintext: Uint8Array, _peerEncPub: string, _mySignPriv: Uint8Array): Promise<SealedPayload> {
    throw new MeshLinkError('E_NOT_IMPLEMENTED', 'Signal 会话加密将在 Phase 2 启用');
  }

  async decrypt(_env: ChatEnvelope, _peer: SessionKeys): Promise<Uint8Array> {
    throw new MeshLinkError('E_NOT_IMPLEMENTED', 'Signal 会话加密将在 Phase 2 启用');
  }

  safetyCode(_peer: SessionKeys): string {
    throw new MeshLinkError('E_NOT_IMPLEMENTED', 'Signal 会话加密将在 Phase 2 启用');
  }

  reset(): void {
    // no-op
  }
}
